import React from "react";
import {
  Zap, Phone, Check, Globe, Star, Shield, Leaf,
} from "lucide-react";
import FlyerPage from "./FlyerPage";
import FlyerLogo from "./FlyerLogo";

const FIXES = [
  "Slow / freezing PCs and laptops",
  "Wi-Fi dropouts and dead zones",
  "Email + Microsoft account recovery",
  "Virus, ransomware & scam cleanup",
  "New PC & printer setup",
  "Home Wi-Fi troubleshooting",
];

const QR_URL =
  "https://api.qrserver.com/v1/create-qr-code/?data=https%3A%2F%2Ftechemergency.com.au%2F&size=240x240&margin=0&color=050505&bgcolor=ffffff&qzone=1";

export default function FlyerResidents() {
  return (
    <FlyerPage title="Flyer — Adelaide Residents" testid="flyer-residents">
      {/* === Header: diagonal red wedge with logo === */}
      <div className="relative -mx-[12mm] -mt-[14mm] mb-5">
        <div
          className="px-[12mm] pt-[12mm] pb-7 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, var(--te-primary) 0%, #d12826 100%)",
          }}
        >
          {/* subtle noise pattern */}
          <div
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0 1px, transparent 1px 8px)",
            }}
          />
          <div className="relative flex items-center justify-between gap-3">
            <FlyerLogo size="lg" />
            <div className="flex items-center gap-1 text-[9.5px] font-bold tracking-[0.18em] uppercase text-white/90 bg-black/30 px-2 py-1">
              <Star size={9} fill="#fff" stroke="#fff" />
              4.9 · GOOGLE
            </div>
          </div>
        </div>
        {/* diagonal cut */}
        <svg
          viewBox="0 0 148 8"
          preserveAspectRatio="none"
          className="block w-full h-3"
          style={{ marginTop: "-1px" }}
        >
          <polygon points="0,0 148,0 148,2 0,8" fill="#d12826" />
        </svg>
      </div>

      {/* === Headline === */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="inline-block w-5 h-[2px]"
            style={{ background: "var(--te-primary)" }}
          />
          <span className="text-[9px] tracking-[0.3em] font-bold uppercase text-zinc-400">
            On-site · Same-day · Adelaide-wide
          </span>
        </div>
        <h1 className="text-[30px] font-black leading-[0.92] tracking-tighter">
          When your tech<br />
          breaks,{" "}
          <span
            className="relative inline-block"
            style={{ color: "var(--te-primary)" }}
          >
            a local human
            <span
              className="absolute left-0 right-0 bottom-0 h-[3px]"
              style={{ background: "var(--te-primary)", opacity: 0.4 }}
            />
          </span>
          <br />
          answers the phone.
        </h1>
      </div>

      <p className="text-[12px] text-zinc-300 leading-snug mb-5">
        Slow computer. Dodgy Wi-Fi. Forgotten password. Suspected scam. Don&apos;t
        lose half a day on hold to overseas support — we&apos;ll come to your door.
      </p>

      {/* === What we fix — split grid === */}
      <div className="mb-5">
        <div className="text-[9px] tracking-[0.24em] font-bold uppercase text-zinc-500 mb-2.5 flex items-center gap-2">
          <span
            className="w-3 h-[2px]"
            style={{ background: "var(--te-primary)" }}
          />
          What we fix
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          {FIXES.map((f) => (
            <div key={f} className="flex items-start gap-1.5 text-[11px] leading-snug">
              <Check
                size={11}
                color="#fff"
                strokeWidth={3}
                style={{ marginTop: 2, color: "var(--te-primary)" }}
              />
              <span>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* === The hero offer ($0 CALLOUT FEE — massive offset treatment) === */}
      <div
        className="relative mb-5 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,59,48,0.18) 0%, rgba(255,59,48,0.04) 100%)",
          border: "1px solid rgba(255,59,48,0.4)",
        }}
      >
        <div
          className="absolute -right-2 -top-2 opacity-15 select-none"
          style={{ color: "var(--te-primary)" }}
        >
          <Zap size={84} strokeWidth={1.5} />
        </div>
        <div className="relative px-4 py-3.5">
          <div className="flex items-baseline gap-2 mb-1">
            <span
              className="text-[44px] font-black tracking-tighter leading-none"
              style={{
                color: "var(--te-primary)",
                textShadow: "0 0 12px rgba(255,59,48,0.4)",
              }}
            >
              $0
            </span>
            <span className="text-[14px] font-extrabold tracking-tight leading-none">
              CALLOUT FEE.<br />
              <span className="text-zinc-400 text-[11.5px] font-bold">
                Ever.
              </span>
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2.5 text-[9.5px] leading-tight text-zinc-300">
            <div className="flex items-center gap-1.5">
              <Shield
                size={10}
                style={{ color: "var(--te-primary)" }}
              />
              <span>No fix · No fee</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Check
                size={10}
                style={{ color: "var(--te-primary)" }}
              />
              <span>Fixed quote upfront</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Leaf
                size={10}
                style={{ color: "var(--te-primary)" }}
              />
              <span>100% electric fleet</span>
            </div>
          </div>
        </div>
      </div>

      {/* === Phone CTA + QR code === */}
      <div
        className="grid grid-cols-[1fr_auto] gap-3 mb-3 items-stretch"
      >
        <div
          className="px-4 py-3.5 flex flex-col justify-center"
          style={{
            background: "var(--te-primary)",
          }}
        >
          <div className="text-[9px] tracking-[0.24em] font-bold uppercase opacity-90 mb-1">
            / Adelaide mobile IT · Free phone triage
          </div>
          <div className="flex items-center gap-1.5 mb-1">
            <Phone size={20} strokeWidth={2.8} />
            <span className="text-[26px] font-black tracking-tight leading-none">
              1800 836 390
            </span>
          </div>
          <div className="text-[10px] font-semibold opacity-90 flex items-center gap-1">
            <Globe size={9} /> techemergency.com.au
          </div>
        </div>
        <div className="bg-white p-1.5 flex flex-col items-center justify-center w-[68px]">
          <img
            src={QR_URL}
            alt="Scan to visit techemergency.com.au"
            className="w-[55px] h-[55px]"
            crossOrigin="anonymous"
          />
          <div className="text-[6.5px] font-bold tracking-[0.12em] uppercase text-black mt-0.5 text-center leading-tight">
            Scan me
          </div>
        </div>
      </div>

      {/* === Footer === */}
      <div className="text-[8.5px] text-center text-zinc-500 leading-snug tracking-[0.08em] uppercase border-t border-white/10 pt-2.5">
        Police-checked techs ·  100% electric fleet ·  Same-day across Adelaide
      </div>
    </FlyerPage>
  );
}
